import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

const INPUT = 'w-full px-3 py-2 text-[13px] border border-[#e5e5e5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3fb5d2] focus:border-transparent text-[#3b3b3b] placeholder-[#c0c0c0] bg-white resize-none';
const LABEL = 'block text-[12px] font-medium text-[#646464] mb-1';

const OPTIONAL_FIELDS = [
  { id: 'product',       label: 'Product',                  rows: 3, placeholder: 'Nama atau deskripsi produk' },
  { id: 'usp',           label: 'USP',                      rows: 3, placeholder: 'Unique Selling Proposition' },
  { id: 'pillar',        label: 'Pillar',                   rows: 3, placeholder: 'Campaign pillar' },
  { id: 'importantTerm', label: 'Important Term',           rows: 3, placeholder: 'Term penting' },
  { id: 'makro',         label: 'Makro',                    rows: 3, placeholder: 'Informasi makro' },
  { id: 'notes',         label: 'Notes',                    rows: 4, placeholder: 'Catatan tambahan' },
];

const CreateCampaign = () => {
  const navigate = useNavigate();
  const [name, setName]                           = useState('');
  const [description, setDescription]             = useState('');
  const [participantQuota, setParticipantQuota]   = useState('');
  const [product, setProduct]                     = useState('');
  const [usp, setUsp]                             = useState('');
  const [notes, setNotes]                         = useState('');
  const [pillar, setPillar]                       = useState('');
  const [importantTerm, setImportantTerm]         = useState('');
  const [makro, setMakro]                         = useState('');
  const [imageFile, setImageFile]                 = useState(null);
  const [imagePreview, setImagePreview]           = useState('');
  const [uploadingImage, setUploadingImage]       = useState(false);
  const [loading, setLoading]                     = useState(false);
  const [error, setError]                         = useState('');

  const getters = { product, usp, notes, pillar, importantTerm, makro };
  const setters = { product: setProduct, usp: setUsp, notes: setNotes, pillar: setPillar, importantTerm: setImportantTerm, makro: setMakro };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('File harus berupa gambar'); return; }
    if (file.size > 5 * 1024 * 1024)    { setError('Ukuran file maksimal 5MB'); return; }
    setError(''); setImageFile(file); setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      let imageUrl = null;
      if (imageFile) {
        setUploadingImage(true);
        const base64 = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result);
          r.onerror = reject;
          r.readAsDataURL(imageFile);
        });
        const { image_url } = await api.post('/api/campaigns/upload-image', { image_base64: base64, filename: imageFile.name });
        imageUrl = image_url;
        setUploadingImage(false);
      }
      const payload = { name, description };
      if (imageUrl) payload.image_url = imageUrl;
      if (participantQuota?.trim()) {
        const quota = parseInt(participantQuota);
        if (isNaN(quota) || quota < 1) { setError('Kuota link harus berupa angka positif'); setLoading(false); return; }
        payload.participant_quota = quota;
      }
      if (product?.trim())        payload.product        = product.trim();
      if (usp?.trim())            payload.usp            = usp.trim();
      if (notes?.trim())          payload.notes          = notes.trim();
      if (pillar?.trim())         payload.pillar         = pillar.trim();
      if (importantTerm?.trim())  payload.important_term = importantTerm.trim();
      if (makro?.trim())          payload.makro          = makro.trim();
      await api.post('/api/campaigns', payload);
      navigate('/campaigns');
    } catch (err) {
      setError(err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="w-full min-w-0 max-w-2xl space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-[22px] font-semibold text-[#3b3b3b] leading-8">Create Campaign</h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 space-y-5" style={{ boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.04)' }}>
        {error && (
          <div className="bg-red-50 border border-red-200 text-[13px] text-[#c62828] px-4 py-3 rounded-xl">{error}</div>
        )}

        {/* Name */}
        <div>
          <label className={LABEL}>Campaign Name *</label>
          <textarea required rows={2} value={name} onChange={(e) => setName(e.target.value)}
            className={INPUT} placeholder="Masukkan nama campaign" />
        </div>

        {/* Description */}
        <div>
          <label className={LABEL}>Description *</label>
          <textarea required rows={5} value={description} onChange={(e) => setDescription(e.target.value)}
            className={INPUT} placeholder="Masukkan deskripsi campaign" />
        </div>

        {/* Image */}
        <div>
          <label className={LABEL}>Gambar Campaign <span className="text-[#8a8a8a] font-normal">(Opsional)</span></label>
          <input type="file" accept="image/*" onChange={handleImageChange}
            className="w-full text-[13px] text-[#646464] file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-[12px] file:font-medium file:bg-[#ecf8fb] file:text-[#2a798c] hover:file:bg-[#3fb5d2]/10 cursor-pointer" />
          {imagePreview && (
            <div className="mt-2">
              <img src={imagePreview} alt="Preview" className="max-h-40 rounded-xl border border-[#e5e5e5] object-cover" />
              <button type="button" onClick={() => { setImageFile(null); setImagePreview(''); }}
                className="mt-1 text-[12px] text-[#c62828] hover:text-red-700 transition-colors">
                Hapus gambar
              </button>
            </div>
          )}
          <p className="text-[11px] text-[#8a8a8a] mt-1">Maks. 5MB · JPG, PNG, WebP</p>
        </div>

        {/* Quota */}
        <div>
          <label className={LABEL}>Kuota Link <span className="text-[#8a8a8a] font-normal">(Opsional)</span></label>
          <input type="number" min="1" value={participantQuota} onChange={(e) => setParticipantQuota(e.target.value)}
            className={INPUT} placeholder="Kosongkan untuk unlimited" />
          <p className="text-[11px] text-[#8a8a8a] mt-1">Maksimal link yang bisa di-submit. Kosongkan untuk unlimited.</p>
        </div>

        {/* Optional fields */}
        <div className="border-t border-[#e5e5e5] pt-5 space-y-5">
          <p className="text-[12px] font-medium text-[#8a8a8a] uppercase tracking-wider">Informasi Tambahan (Opsional)</p>
          {OPTIONAL_FIELDS.map(({ id, label, rows, placeholder }) => (
            <div key={id}>
              <label className={LABEL}>{label}</label>
              <textarea rows={rows} value={getters[id]} onChange={(e) => setters[id](e.target.value)}
                className={INPUT} placeholder={placeholder} />
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={() => navigate('/campaigns')}
            className="px-4 py-2 text-[13px] font-medium text-[#646464] border border-[#e5e5e5] rounded-lg hover:bg-gray-50 transition-colors">
            Batal
          </button>
          <button type="submit" disabled={loading || uploadingImage}
            className="px-5 py-2 text-[13px] font-semibold bg-[#3fb5d2] hover:bg-[#2a798c] text-white rounded-lg transition-colors disabled:opacity-50">
            {uploadingImage ? 'Uploading...' : loading ? 'Membuat...' : 'Create Campaign'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateCampaign;
